import ApiService from './api'

class WarehouseService {
  /**
   * Get all warehouses
   * @param {Object} params - Query parameters
   * @returns {Promise} API response
   */
  async getAllWarehouses(params = {}) {
    try {
      const response = await ApiService.get('/warehouse-transfer-notes', { params })
      return response.data
    } catch (error) {
      console.error('Error fetching warehouses:', error)
      
      // If warehouse endpoints are not implemented yet (404), return mock data
      if (error.message === 'Not found' || error.response?.status === 404) {
        console.log('🔧 Warehouse endpoints not available, using mock data')
        return {
          success: true,
          data: {
            warehouses: [
              {
                _id: 'mock-warehouse-1',
                name: 'Main Warehouse',
                location: 'Downtown',
                capacity: 1000,
                currentStock: 750,
                isActive: true,
                createdAt: new Date().toISOString()
              },
              {
                _id: 'mock-warehouse-2', 
                name: 'Secondary Warehouse',
                location: 'Industrial Zone',
                capacity: 800,
                currentStock: 600,
                isActive: true,
                createdAt: new Date().toISOString()
              }
            ],
            total: 2,
            page: 1,
            limit: 10
          },
          message: 'Mock warehouse data (Backend endpoints not implemented yet)'
        }
      }
      
      throw error
    }
  }

  /**
   * Get warehouse by ID
   * @param {string} id - Warehouse ID
   * @returns {Promise} API response
   */
  async getWarehouseById(id) {
    try {
      const response = await ApiService.get(`/warehouse-transfer-notes/${id}`)
      return response.data
    } catch (error) {
      console.error('Error fetching warehouse:', error)
      
      // If warehouse endpoints are not implemented yet (404), return mock data
      if (error.message === 'Not found' || error.response?.status === 404) {
        console.log('🔧 Warehouse endpoint not available, using mock data for ID:', id)
        return {
          success: true,
          data: {
            _id: id,
            name: id === 'mock-warehouse-1' ? 'Main Warehouse' : 'Secondary Warehouse',
            location: id === 'mock-warehouse-1' ? 'Downtown' : 'Industrial Zone',
            capacity: id === 'mock-warehouse-1' ? 1000 : 800,
            currentStock: id === 'mock-warehouse-1' ? 750 : 600,
            isActive: true,
            createdAt: new Date().toISOString()
          },
          message: 'Mock warehouse data (Backend endpoints not implemented yet)'
        }
      }
      
      throw error
    }
  }

  /**
   * Create a new warehouse
   * @param {Object} warehouseData - Warehouse data
   * @returns {Promise} API response
   */
  async createWarehouse(warehouseData) {
    try {
      const response = await ApiService.post('/warehouse-transfer-notes', warehouseData)
      return response.data
    } catch (error) {
      console.error('Error creating warehouse:', error)
      
      // If warehouse endpoints are not implemented yet (404), return mock success
      if (error.message === 'Not found' || error.response?.status === 404) {
        console.log('🔧 Warehouse create endpoint not available, returning mock success')
        return {
          success: true,
          data: {
            _id: `mock-warehouse-${Date.now()}`,
            ...warehouseData,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          },
          message: 'Mock warehouse created (Backend endpoints not implemented yet)'
        }
      }
      
      throw error
    }
  }

  /**
   * Update warehouse by ID
   * @param {string} id - Warehouse ID
   * @param {Object} updateData - Updated warehouse data
   * @returns {Promise} API response
   */
  async updateWarehouse(id, updateData) {
    try {
      const response = await ApiService.put(`/warehouse-transfer-notes/${id}`, updateData)
      return response.data
    } catch (error) {
      console.error('Error updating warehouse:', error)
      
      // If warehouse endpoints are not implemented yet (404), return mock success
      if (error.message === 'Not found' || error.response?.status === 404) {
        console.log('🔧 Warehouse update endpoint not available, returning mock success')
        return {
          success: true,
          data: {
            _id: id,
            ...updateData,
            updatedAt: new Date().toISOString()
          },
          message: 'Mock warehouse updated (Backend endpoints not implemented yet)'
        }
      }
      
      throw error
    }
  }

  /**
   * Delete warehouse by ID
   * @param {string} id - Warehouse ID
   * @returns {Promise} API response
   */
  async deleteWarehouse(id) {
    try {
      const response = await ApiService.delete(`/warehouse-transfer-notes/${id}`)
      return response.data
    } catch (error) {
      console.error('Error deleting warehouse:', error)
      
      // If warehouse endpoints are not implemented yet (404), return mock success
      if (error.message === 'Not found' || error.response?.status === 404) {
        console.log('🔧 Warehouse delete endpoint not available, returning mock success')
        return {
          success: true,
          data: { deletedId: id },
          message: 'Mock warehouse deleted (Backend endpoints not implemented yet)'
        }
      }
      
      throw error
    }
  }

  /**
   * Get warehouse statistics
   * @returns {Promise} API response
   */
  async getWarehouseStats() {
    try {
      const response = await ApiService.get('/warehouse-transfer-notes')
      return response.data
    } catch (error) {
      console.error('Error fetching warehouse stats:', error)
      
      // If warehouse endpoints are not implemented yet (404), return mock stats
      if (error.message === 'Not found' || error.response?.status === 404) {
        console.log('🔧 Warehouse stats endpoint not available, using mock data')
        return {
          success: true,
          data: {
            totalWarehouses: 2,
            activeWarehouses: 2,
            inactiveWarehouses: 0,
            totalCapacity: 1800,
            totalStock: 1350,
            utilizationRate: 75,
            lastUpdated: new Date().toISOString()
          },
          message: 'Mock warehouse stats (Backend endpoints not implemented yet)'
        }
      }
      
      throw error
    }
  }
}

export default new WarehouseService()